/**
 * Tile recognition client.
 *
 * This calls the relay, never the Claude API directly. The API key must not
 * reach the browser bundle. See relay/index.ts.
 *
 * Recognition is treated as a *suggestion*, not a source of truth: every
 * result carries per-tile confidence and goes through the manual correction
 * step before scoring locks in.
 */

import type { BonusTile, Tile } from '../rules/tiles';

export interface RecognisedTile {
  tile: Tile;
  /** 0..1. Low confidence tiles get highlighted in the correction UI. */
  confidence: number;
  /** Bounding box in the source image, for drawing the correction overlay. */
  box?: { x: number; y: number; w: number; h: number };
}

export interface RecognisedHand {
  seat: 'east' | 'south' | 'west' | 'north';
  tiles: RecognisedTile[];
  /**
   * Kongs are detected from spacing: four identical tiles laid flat with a
   * visible gap from neighbouring sets. Unreliable enough that the manual
   * flag stays available as a backup — testing decides which becomes default.
   */
  suspectedKongs: Tile[][];
  /** Specific flower/season tiles seen, e.g. ["F1","S3"] — which one matters for the seat/round bonus. */
  flowers: BonusTile[];
}

export interface RecogniseRequest {
  /** Base64 data URL(s). One photo of the whole table, or one per seat. */
  images: string[];
  /** Seat order when sending four individual photos. */
  seatOrder?: Array<'east' | 'south' | 'west' | 'north'>;
  tableId: string;
}

export interface RecogniseResponse {
  hands: RecognisedHand[];
  /** Model's own warnings: stacked tiles, glare, partial occlusion. */
  warnings: string[];
  /** Token usage for this call, if the relay could surface it — for the local usage log. */
  usage?: { inputTokens: number; outputTokens: number };
}

const RELAY_URL = import.meta.env?.VITE_VISION_RELAY_URL ?? '/api/recognize';

export type RecognitionFailureKind = 'insufficient_credit' | 'rate_limited' | 'other';

export class RecognitionError extends Error {
  readonly kind: RecognitionFailureKind;
  readonly status: number;

  constructor(kind: RecognitionFailureKind, status: number, message: string) {
    super(message);
    this.name = 'RecognitionError';
    this.kind = kind;
    this.status = status;
  }
}

interface RelayErrorBody {
  error?: string;
  status?: number;
  upstreamType?: string;
  upstreamMessage?: string;
}

/**
 * Best-effort only — Anthropic's exact error.type for "out of credit" on this
 * account type isn't confirmed, so this matches status + keywords rather than
 * an official code. Always resolves to a bucket; 'other' is the safe fallback
 * and must still produce a useful message on its own.
 */
export function classifyRecognitionError(
  status: number,
  body: RelayErrorBody | undefined,
): RecognitionFailureKind {
  const type = body?.upstreamType ?? '';
  const message = (body?.upstreamMessage ?? '').toLowerCase();

  if (status === 429 || type === 'rate_limit_error' || type === 'overloaded_error') {
    return 'rate_limited';
  }
  if (message.includes('credit balance') || message.includes('insufficient')) {
    return 'insufficient_credit';
  }
  return 'other';
}

/**
 * `token` is the device's activation session (see src/ui/activation.ts) —
 * required, not optional, since the relay now rejects recognition calls
 * with no valid session. Kept as an explicit parameter rather than read
 * from here directly to keep this module free of any src/ui/ dependency.
 */
export async function recogniseTiles(
  req: RecogniseRequest,
  token: string,
  signal?: AbortSignal,
): Promise<RecogniseResponse> {
  const res = await fetch(RELAY_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(req),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok) {
    let body: RelayErrorBody | undefined;
    try {
      body = (await res.json()) as RelayErrorBody;
    } catch {
      // Upstream/relay didn't return JSON — classifyRecognitionError handles undefined fine.
    }
    const kind = classifyRecognitionError(res.status, body);
    const message = body?.upstreamMessage ?? `Vision relay failed: ${res.status} ${res.statusText}`;
    throw new RecognitionError(kind, res.status, message);
  }
  return (await res.json()) as RecogniseResponse;
}

/** Confidence below this gets flagged for the human to check. */
export const LOW_CONFIDENCE_THRESHOLD = 0.85;
